/**
 * TCR — Favoris / wishlists.
 *
 * Deux modes derrière une seule API :
 *  - visiteur non connecté : une liste unique en localStorage, aucun appel réseau
 *  - client connecté       : N listes nommées en base, via l'app proxy B2B
 *
 * Au login, la liste localStorage est fusionnée dans la liste par défaut du
 * compte puis effacée localement (action `merge` côté serveur, non destructive).
 *
 * Le proxy est appelé sur le chemin RACINE (/apps/tcr-b2b/wishlist), jamais sur
 * un chemin préfixé locale (/fr-fr/apps/...) : le Worker Cloudflare
 * `tcr-edge-redirects` écoute sur /xx-xx/* et intercepterait les requêtes.
 *
 * État exposé, identique dans les deux modes :
 *   { loggedIn, lists: [{ id, name, isDefault, items: [...] }] }
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tcr:wishlist:v1';
  var CHANGE_EVENT = 'tcr:wishlist:change';
  var LOCAL_LIST_ID = 'local';

  var cfg = window.tcrWishlistConfig || {};
  var state = { loggedIn: !!cfg.loggedIn, lists: [] };
  var ready = false;

  // ─── localStorage ──────────────────────────────────────────────────────────
  // Safari en navigation privée fait throw sur setItem : toute la couche de
  // stockage est défensive, une erreur ne doit jamais casser la page.

  function readLocal() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeLocal(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* quota plein ou navigation privée : on continue sans persister */
    }
  }

  function clearLocal() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function localList(items) {
    return [
      {
        id: LOCAL_LIST_ID,
        name: cfg.defaultListName || 'Mes favoris',
        isDefault: true,
        items: items,
      },
    ];
  }

  // ─── Réseau ────────────────────────────────────────────────────────────────

  function proxy(method, body) {
    var url = cfg.proxyUrl || '/apps/tcr-b2b/wishlist';
    var opts = {
      method: method,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('wishlist-http-' + r.status);
      return r.json();
    });
  }

  function commit(payload) {
    if (payload && Array.isArray(payload.lists)) {
      state.lists = payload.lists;
    }
    emit();
    return state;
  }

  function emit() {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { state: state, ready: ready } })
    );
  }

  // ─── Lecture ───────────────────────────────────────────────────────────────

  function allItems() {
    return state.lists.reduce(function (acc, l) {
      return acc.concat(l.items || []);
    }, []);
  }

  function has(variantId) {
    return allItems().some(function (i) {
      return i.variantId === variantId;
    });
  }

  function count() {
    // Une même variante peut être dans plusieurs listes : on compte les
    // variantes distinctes, sinon le badge du header ment.
    var seen = {};
    allItems().forEach(function (i) {
      seen[i.variantId] = true;
    });
    return Object.keys(seen).length;
  }

  // ─── Écriture ──────────────────────────────────────────────────────────────

  function add(item, listId) {
    if (!item || !item.variantId || !item.productId) {
      return Promise.reject(new Error('invalid-item'));
    }
    if (!state.loggedIn) {
      var items = readLocal().filter(function (i) {
        return i.variantId !== item.variantId;
      });
      items.unshift(item);
      writeLocal(items);
      state.lists = localList(items);
      emit();
      return Promise.resolve(state);
    }
    return proxy('POST', {
      action: 'add',
      item: item,
      wishlistId: listId || null,
    }).then(commit);
  }

  function remove(variantId, listId) {
    if (!state.loggedIn) {
      var items = readLocal().filter(function (i) {
        return i.variantId !== variantId;
      });
      writeLocal(items);
      state.lists = localList(items);
      emit();
      return Promise.resolve(state);
    }
    return proxy('POST', {
      action: 'remove',
      variantId: variantId,
      wishlistId: listId || null,
    }).then(commit);
  }

  /** Retourne true si l'item est désormais en favori, false sinon. */
  function toggle(item) {
    var added = !has(item.variantId);
    var p = added ? add(item) : remove(item.variantId);
    return p.then(function () {
      return added;
    });
  }

  function mutate(action, payload) {
    if (!state.loggedIn) return Promise.reject(new Error('not-logged-in'));
    var body = payload || {};
    body.action = action;
    return proxy('POST', body).then(commit);
  }

  // ─── Amorçage ──────────────────────────────────────────────────────────────

  function boot() {
    if (!state.loggedIn) {
      state.lists = localList(readLocal());
      ready = true;
      emit();
      return Promise.resolve(state);
    }

    var pending = readLocal();

    // Client connecté avec un reliquat localStorage : on fusionne d'abord, la
    // réponse du merge contient déjà l'état complet (pas de second aller-retour).
    if (pending.length) {
      return proxy('POST', { action: 'merge', items: pending })
        .then(function (res) {
          clearLocal();
          ready = true;
          return commit(res);
        })
        .catch(function () {
          // Le merge a échoué : on GARDE le localStorage pour retenter au
          // prochain chargement plutôt que de perdre les favoris du visiteur.
          return load();
        });
    }
    return load();
  }

  function load() {
    return proxy('GET')
      .then(function (res) {
        state.loggedIn = !!res.loggedIn;
        ready = true;
        return commit(res);
      })
      .catch(function () {
        // Backend indisponible : on dégrade sur le localStorage plutôt que
        // d'afficher une wishlist vide, qui donnerait l'impression d'une perte.
        state.lists = localList(readLocal());
        ready = true;
        emit();
        return state;
      });
  }

  window.TCRWishlist = {
    CHANGE_EVENT: CHANGE_EVENT,
    getState: function () {
      return state;
    },
    isReady: function () {
      return ready;
    },
    has: has,
    count: count,
    add: add,
    remove: remove,
    toggle: toggle,
    createList: function (name) {
      return mutate('createList', { name: name });
    },
    renameList: function (id, name) {
      return mutate('renameList', { wishlistId: id, name: name });
    },
    deleteList: function (id) {
      return mutate('deleteList', { wishlistId: id });
    },
    moveItem: function (variantId, fromListId, toListId) {
      return mutate('moveItem', {
        variantId: variantId,
        fromListId: fromListId,
        toListId: toListId,
      });
    },
    updateItem: function (listId, variantId, data) {
      return mutate('updateItem', {
        wishlistId: listId,
        variantId: variantId,
        quantity: data.quantity,
        note: data.note,
      });
    },
    reload: load,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
